import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, ViewChild, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Lang, LangService } from '../../../core/services/lang.service';
import { LocalizedRoutesService } from '../../../core/routing/localized-routes.service';
import { RouteKey } from '../../../core/routing/localized-routes';
import { CookieConsentService } from '../../../core/services/cookie-consent.service';

type NavItem = {
  labelKey: string;
  routeKey: RouteKey;
};

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslatePipe],
  templateUrl: './header.html'
})
export class HeaderComponent implements OnDestroy {
  readonly paths = inject(LocalizedRoutesService);
  readonly consent = inject(CookieConsentService);

  @ViewChild('candidatDropdown') candidatDropdownRef?: ElementRef<HTMLElement>;
  @ViewChild('langMenu') langMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('mobileLangMenu') mobileLangMenuRef?: ElementRef<HTMLElement>;
  @ViewChild('mobileMenuContainer') mobileMenuContainerRef?: ElementRef<HTMLElement>;

  candidatMenuOpen = false;
  mobileMenuOpen = false;
  langMenuOpen = false;
  lang: Lang = 'fr';

  private langSub: Subscription;

  readonly supportEmail = 'support@unelya.com';

  readonly items: NavItem[] = [
    { labelKey: 'HEADER.NAV.HOME', routeKey: 'home' },
    { labelKey: 'HEADER.NAV.PROGRAMS', routeKey: 'programs' },
    { labelKey: 'HEADER.NAV.DEGREE_RECOGNITION', routeKey: 'degreeRecognition' },
    { labelKey: 'HEADER.NAV.ABOUT', routeKey: 'about' }
  ];

  constructor(private langService: LangService) {
    this.langSub = this.langService.lang$.subscribe(l => this.lang = l);
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
  }

  toggleLangMenu(): void {
    this.langMenuOpen = !this.langMenuOpen;
  }

  setLang(next: Lang): void {
    this.langService.set(next);
    this.langMenuOpen = false;
  }

  toggleCandidatMenu(): void {
    this.candidatMenuOpen = !this.candidatMenuOpen;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.langMenuOpen = false;
  }

  closeCandidatMenu(): void {
    this.candidatMenuOpen = false;
  }

  openSupportEmail(): void {
    this.candidatMenuOpen = false;
    this.closeMobileMenu();
    window.location.href = `mailto:${this.supportEmail}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node;
    if (
      this.candidatDropdownRef?.nativeElement &&
      !this.candidatDropdownRef.nativeElement.contains(target)
    ) {
      this.candidatMenuOpen = false;
    }
    const inLangMenu =
      (this.langMenuRef?.nativeElement?.contains(target) ?? false) ||
      (this.mobileLangMenuRef?.nativeElement?.contains(target) ?? false);
    if (!inLangMenu) {
      this.langMenuOpen = false;
    }
    if (
      this.mobileMenuContainerRef?.nativeElement &&
      !this.mobileMenuContainerRef.nativeElement.contains(target)
    ) {
      this.mobileMenuOpen = false;
    }
  }
}
